import { BASE_URL } from '../../const';
import { store } from '../../store';
import { logOut } from '../../store/slices/authSlice';
import { setShowReconnectAnimation } from '../../store/slices/chatsSlice';
import LoggerService from '../LoggerService';

export const isLocal = process.env.REACT_APP_ENVIRONMENT === 'local';

const MILLIS_IN_SEC = 1000;

const log = (msg, ...args) => {
    LoggerService.log('WebSocketConnector: ' + msg, ...args);
};

class WebSocketConnector {
    #_store = null;
    autoReconnectInterval;
    socket = null;
    onMessageHandler = null;
    onOpen;
    onCloseHandler = null;
    token = null;
    reconnectTimeout = null;
    lastApiRequestOnReconnectTimestamp = Date.now();

    constructor() {
        this.autoReconnectInterval = 5 * MILLIS_IN_SEC; // 5s
        this.onOpen = () => {};
    }

    get ws() {
        return this.socket;
    }

    set ws(socket) {
        this.socket = socket;
    }

    init(token) {
        log('init', { token, store });
        this.reopenWithToken(token);
    }

    set store(store) {
        this.#_store = store;
    }

    get store() {
        if (!this.#_store) {
            throw new Error('Failed to get store');
        }
        return this.#_store;
    }

    setOnClose(onClose) {
        this.onCloseHandler = onClose;
    }

    open() {
        log('open:init');
        if (this.ws?.removeEventListener) {
            log('open:remove_old_listeners');
            this.ws.removeEventListener('message', this.onMessage);
            this.ws.removeEventListener('open', this.onOpen);
            this.ws.removeEventListener('close', this.onClose);
            this.ws.removeEventListener('error', this.onError);
        }

        log('open:start_connect');
        this.ws = new WebSocket(this.getWsUrl());
        this.ws.addEventListener('message', this.onMessage);
        this.ws.addEventListener('open', this.onOpen);
        this.ws.addEventListener('close', this.onClose);
        this.ws.addEventListener('error', this.onError);
    }

    reopenWithToken(token) {
        log('reopenWithToken:init', { token });
        const tokenFromStore = token;
        if (tokenFromStore === this.token) {
            log('reopenWithToken:equals:END', { token });
            return;
        }

        log('reopenWithToken:update', { token });
        this.token = tokenFromStore;
        this.ws?.close(1000, 'Performing reconnect');
        this.open();
    }

    getWsUrl() {
        const apiUrl = isLocal ? `ws://${new URL(BASE_URL).host}` : `wss://${new URL(BASE_URL).host}`;
        const url = `${apiUrl}?token=${this.token}`;
        log('getWsUrl:init', { url });

        return url;
    }

    onMessage = (wsEventData) => {
        log('onMessage:init', { wsEventData });
        if (typeof this.onMessageHandler === 'function') {
            log('onMessage:callback', { wsEventData, onMessageHandler: this.onMessageHandler });
            this.onMessageHandler(wsEventData);
        }
    };

    onClose = (closeEvent) => {
        log('onClose:init', { closeEvent });
        if (this.onCloseHandler) {
            this.onCloseHandler(closeEvent);
        }

        switch (closeEvent.code) {
            case 1000: // CLOSE_NORMAL
                console.log('WEBSOCKET:Normal closing');
                break;
            case 3000: // CLOSE_DUE_TO_RELOGIN
                logOut();
                break;
            default:
                this.reconnect();
                break;
        }
    };

    onError = (errorEvent) => {
        log('onError:init', { errorEvent });

        switch (errorEvent.type) {
            case 'ECONNREFUSED':
                this.reconnect();
                break;
            default:
                this.reconnect();
                break;
        }
    };

    send(data) {
        log('send:init', { data });
        // todo: add queue for requests while the connection restarts
        try {
            this.ws?.send(JSON.stringify(data));
        } catch (e) {
            this.onError(e);
        }
    }

    reconnect() {
        log('reconnect:init');
        store.dispatch(setShowReconnectAnimation(true));
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        this.reconnectTimeout = setTimeout(() => {
            log('reconnect:autoReconnectInterval');
            this.open();
        }, this.autoReconnectInterval);
    }

    get readyState() {
        return this.ws?.readyState;
    }

    registerMessageHandler(onMessageHandler) {
        this.onMessageHandler = onMessageHandler;
    }
}

export default new WebSocketConnector();
